import type { WebPageSection } from '@/lib/api'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { parseHtmlString } from '@/utils/helpers/html'

interface InfoCard {
  title: string
  description: string
  image: string
  href: string
}

type InfoCardProps = InfoCard

const InfoCard = ({ title, description, image, href }: InfoCardProps) => (
  <div className="bg-gray-50 p-4 lg:p-6 h-full">
    <div className="flex flex-col h-full space-y-8">
      <img 
        src={image} 
        alt={title}
        className="w-full aspect-video object-cover"
      />
      
      <div className="flex-1 space-y-4">
        <h3 className="text-2xl font-normal font-pt-serif text-gray-900 leading-7">
          {title}
        </h3>
        <div className="font-satoshi text-gray-900 line-clamp-6" dangerouslySetInnerHTML={parseHtmlString(description)}>
        </div>
      </div>
      
      <Link 
        to={href}
        className="inline-flex items-center gap-1.5 text-gray-900 hover:text-wine-100 transition-colors group"
      >
        <span className="text-sm font-medium font-satoshi">Learn More</span>
        <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  </div>
)

interface InfoCardGridProps {
  className?: string,
  data: {
    [key: string]: WebPageSection
  }
}

const InfoCardGrid = ({ className, data }: InfoCardGridProps) => {
  const infoCards: InfoCard[] = [
    {
      title: `${data.WHO_ARE_WE_HISTORICAL_TIMELINE?.title ?? "Historical Timeline"}`,
      description: `${data.WHO_ARE_WE_HISTORICAL_TIMELINE?.description ?? "African Space Agency's journey, from policy frameworks to headquarters in Egypt, shaping Africa's space ambitions"}`,
      image: `${data.WHO_ARE_WE_HISTORICAL_TIMELINE?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760287/Image-1_xtaaby.png"}`,
      href: "/about/history"
    },
    {
      title: `${data.WHO_ARE_WE_LEGAL_FOUNDATION?.title ?? "Legal Foundation"}`,
      description: `${data.WHO_ARE_WE_LEGAL_FOUNDATION?.description ?? "The African Space Agency Act and policies that govern AfSA's mission and international collaboration."}`,
      image: `${data.WHO_ARE_WE_LEGAL_FOUNDATION?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760287/Image1-1_gl6tuf.png"}`,
      href: "/about/legal-foundation"
    },
    {
      title: `${data.WHO_ARE_WE_LEADERSHIP?.title ?? "Leadership"}`,
      description: `${data.WHO_ARE_WE_LEADERSHIP?.description ?? "Meet the leaders and visionaries driving Africa's space exploration and innovation efforts."}`,
      image: `${data.WHO_ARE_WE_LEADERSHIP?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-2_xbv8lj.png"}`,
      href: "/about/leadership"
    },
    {
      title: `${data.WHO_ARE_WE_MEMBER_COUNTRIES_2?.title ?? "Member Countries"}`,
      description: `${data.WHO_ARE_WE_MEMBER_COUNTRIES_2?.description ?? "Discover the African nations contributing to space science, innovation, and technological advancement."}`,
      image: `${data.WHO_ARE_WE_MEMBER_COUNTRIES_2?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1755763789/b54f8598c43a335b7dd4b845e6049d21ae14acc9_ac9gp7.jpg"}`,
      href: "/about/member-countries"
    }
  ]

  return (
    <section className={className}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {infoCards.map((card, index) => (
          <InfoCard key={index} {...card} />
        ))}
      </div>
    </section>
  )
}

export default InfoCardGrid